import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';

const PAGE_SIZE = 10;

interface CampaignTemplate {
  id: string;
  nome: string;
  messageType: string;
  minDelay: number;
  maxDelay: number;
  atualizadoEm: string;
  messageContent: any;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getMessageTypeLabel(type: string) {
  const types: Record<string, string> = {
    text: 'Texto',
    image: 'Imagem',
    video: 'Vídeo',
    audio: 'Áudio',
    document: 'Documento',
    sequence: 'Sequência',
  };
  return types[type] || type;
}

export function CampaignTemplatesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [allTemplates, setAllTemplates] = useState<CampaignTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();

      if (search.trim()) {
        params.append('search', search.trim());
      }

      const response = await authenticatedFetch(`/api/campaigns/templates?${params}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const templatesList = data.data || [];
      setAllTemplates(templatesList);
    } catch (err) {
      console.error('Erro ao carregar modelos:', err);
      setError('Erro ao carregar modelos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [search]);

  // Filtragem e paginação no frontend
  const filteredTemplates = allTemplates.filter(template =>
    template.nome.toLowerCase().includes(search.toLowerCase())
  );

  const total = filteredTemplates.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageToShow = Math.min(currentPage, totalPages) || 1;

  const startIndex = (pageToShow - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const templates = filteredTemplates.slice(startIndex, endIndex);

  const handleDelete = async (id: string, nome: string) => {
    if (!confirm(`Excluir o modelo "${nome}"?`)) return;

    try {
      const response = await authenticatedFetch(`/api/campaigns/templates/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success('Modelo excluído com sucesso');
      loadTemplates();
    } catch (err) {
      console.error('Erro ao excluir modelo:', err);
      toast.error('Erro ao excluir modelo');
    }
  };

  const handleUseTemplate = (template: CampaignTemplate) => {
    // Parse o messageContent se for string
    const messageContent = typeof template.messageContent === 'string'
      ? JSON.parse(template.messageContent)
      : template.messageContent;

    const templateData = {
      ...template,
      messageContent
    };

    // Salva o template no localStorage para ser carregado na página de criação
    localStorage.setItem('campaign_template_to_load', JSON.stringify(templateData));
    navigate('/campanhas/novo');
    toast.success('Modelo carregado. Configure os detalhes da campanha.');
  };

  if (error) {
    return (
      <>
        <Header title="Modelos de Campanha" subtitle="Erro" compactTopbar />
        <PageLayout>
          <div className="card-padrao p-8 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={() => loadTemplates()}
              className="px-4 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Tentar novamente
            </button>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Header title="Modelos de Campanha" subtitle={`${total} modelo(s)`} compactTopbar />
      <PageLayout>
        <PageHeader
          title="Modelos de Campanha"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Campanhas', href: '/campanhas' },
            { label: 'Modelos' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Modelos Salvos</h2>
              {(total > 0 || search.trim() !== '') && (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative w-48 sm:w-56">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" aria-hidden />
                    <input
                      type="text"
                      placeholder="Buscar por nome..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando...</span>
              </div>
            ) : total === 0 && search.trim() === '' ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4 text-gray-300 dark:text-gray-600" aria-hidden>
                  <i className="fas fa-file-alt" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Nenhum modelo salvo
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Salve modelos de campanhas para reutilizá-los facilmente.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/campanhas/novo')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                >
                  <i className="fas fa-plus text-base" aria-hidden />
                  Criar Campanha
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/campanhas')}
                  className="ml-3 inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Voltar para Campanhas
                </button>
              </div>
            ) : total === 0 && search.trim() !== '' ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                Nenhum modelo encontrado para &quot;{search}&quot;. Altere o termo de busca ou limpe o campo.
              </div>
            ) : templates.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                Nenhum modelo encontrado para &quot;{search}&quot;.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Intervalo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Atualizado em</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                      {templates.map((template) => (
                        <tr key={template.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">{template.nome}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                              {getMessageTypeLabel(template.messageType)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {template.minDelay}s - {template.maxDelay}s
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatDate(template.atualizadoEm)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex gap-2 justify-end">
                              <button
                                type="button"
                                onClick={() => handleUseTemplate(template)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 font-medium transition-colors"
                                title="Usar modelo"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                Usar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(template.id, template.nome)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 font-medium transition-colors"
                                title="Excluir modelo"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Mostrando {(pageToShow - 1) * PAGE_SIZE + 1}–{Math.min(pageToShow * PAGE_SIZE, total)} de {total} modelo(s)
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={pageToShow <= 1}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        Página {pageToShow} de {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={pageToShow >= totalPages}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {total > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/campanhas')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
              >
                <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                Voltar para Campanhas
              </button>
            </div>
          )}
        </div>
      </PageLayout>
    </>
  );
}
